import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { applyPreferences } from '@buckmason/cx-ui';
import { ApiError, api } from './api';
import type { Me } from './types';

type AuthState = {
  me: Me | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<Me>;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
  /** Replace `me` with a fresh copy from the server (after saving preferences, say). */
  setMe: (me: Me) => void;
  isAdmin: boolean;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Appearance and the accent come with the session; the hub applies them like every app does.
  useEffect(() => {
    if (me) applyPreferences({ appearance: me.appearance, tint: me.tint, accent: me.accent });
  }, [me?.appearance, me?.tint, me?.accent]);

  /**
   * Who is signed in. Only a 401 (after the client's refresh attempt) means signed out. Anything
   * else (the hub unreachable, a deploy's dyno swap) keeps what we had and asks again, for about
   * 15 seconds, before giving up; a deploy no longer drops open tabs to Sign in.
   */
  const reload = async (): Promise<void> => {
    // About 15 s in all: well inside the hub's 30 s refresh grace, so a retry after a lost refresh
    // reply never counts as token reuse (which would sign this browser out of every app).
    const waits = [1000, 2000, 4000, 8000];
    for (let attempt = 0; ; attempt++) {
      try {
        const r = await api.get<{ person: Me }>('/api/id/auth/me');
        setMe(r.person);
        return;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setMe(null);
          return;
        }
        if (attempt >= waits.length) return; // keep whatever we had
        await new Promise((r) => setTimeout(r, waits[attempt]));
      }
    }
  };

  useEffect(() => {
    void reload().finally(() => setLoading(false));
  }, []);

  const signIn = async (email: string, password: string) => {
    const r = await api.post<{ person: Me }>('/api/id/auth/login', { email, password });
    setMe(r.person);
    return r.person;
  };

  /** Sign out: of every CX app at once, since they share this session. */
  const signOut = async () => {
    await api.post('/api/id/auth/logout').catch(() => {});
    setMe(null);
    // Back to the defaults, so a shared machine's next person does not start in this person's look.
    applyPreferences({ appearance: 'system', tint: false, accent: 'auto' });
  };

  return (
    <Ctx.Provider value={{ me, loading, signIn, signOut, reload, setMe, isAdmin: me?.hubRole === 'admin' }}>{children}</Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

/**
 * Where to go after signing in. An app sends people here with `?next=<the page they wanted>`;
 * only a page on the hub or on one of this person's apps is honoured, so a crafted link cannot
 * bounce someone to another site straight after they type their password.
 */
export function safeNext(next: string | null, me: Me): string | null {
  if (!next) return null;
  try {
    const url = new URL(next, window.location.origin);
    const allowed = [window.location.origin, ...me.apps.map((a) => new URL(a.url).origin)];
    return allowed.includes(url.origin) ? url.toString() : null;
  } catch {
    return null;
  }
}
